import { useEffect, useId, useMemo, useRef, useState, type ButtonHTMLAttributes } from 'react'

import { cn } from '@/lib/cn'

export interface MultiCheckboxSelectOption {
  label: string
  value: string
}

export interface MultiCheckboxSelectProps extends Omit<
  ButtonHTMLAttributes<HTMLButtonElement>,
  'onChange' | 'value'
> {
  allLabel: string
  onValueChange: (value: string[]) => void
  options: MultiCheckboxSelectOption[]
  value: string[]
}

export function MultiCheckboxSelect({
  allLabel,
  className,
  disabled,
  onValueChange,
  options,
  value,
  ...props
}: MultiCheckboxSelectProps) {
  const [isOpen, setIsOpen] = useState(false)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const rootRef = useRef<HTMLDivElement>(null)
  const menuId = useId()
  const optionValues = useMemo(() => options.map((option) => option.value), [options])
  const selectedValues = useMemo(() => {
    if (value.length === 0) {
      return new Set(optionValues)
    }

    return new Set(value)
  }, [optionValues, value])
  const selectedOptions = options.filter((option) => selectedValues.has(option.value))
  const isAllSelected = options.length === 0 || selectedOptions.length === options.length
  const buttonLabel = isAllSelected
    ? allLabel
    : selectedOptions.map((option) => option.label).join('/')

  useEffect(() => {
    if (!isOpen) {
      return undefined
    }

    function handlePointerDown(event: PointerEvent) {
      if (!rootRef.current?.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    document.addEventListener('pointerdown', handlePointerDown)

    return () => {
      document.removeEventListener('pointerdown', handlePointerDown)
    }
  }, [isOpen])

  function toggleOption(optionValue: string) {
    const nextValues = new Set(selectedValues)
    if (nextValues.has(optionValue)) {
      nextValues.delete(optionValue)
    } else {
      nextValues.add(optionValue)
    }

    const normalizedValues = options
      .filter((option) => nextValues.has(option.value))
      .map((option) => option.value)
    onValueChange(normalizedValues.length === options.length ? [] : normalizedValues)
  }

  return (
    <div
      className={cn(
        'relative h-[38px] w-full text-sm',
        disabled ? 'cursor-not-allowed opacity-70' : null,
        className,
      )}
      ref={rootRef}
    >
      <button
        aria-controls={isOpen ? menuId : undefined}
        aria-expanded={isOpen}
        aria-haspopup="menu"
        className={cn(
          'flex h-full w-full min-w-0 cursor-pointer items-center rounded-lg border bg-white pl-3 pr-9 text-left text-[var(--color-text)] shadow-[var(--shadow-control)] transition-colors outline-none',
          'focus:border-[var(--color-primary)] focus:ring-2 focus:ring-[var(--color-primary-soft)]',
          disabled
            ? 'cursor-not-allowed border-[var(--color-panel-border)] bg-[var(--color-surface-muted)] shadow-none'
            : 'border-[var(--color-control-border)] hover:border-[var(--color-control-border-hover)]',
          isAllSelected ? 'text-[rgba(26,28,25,0.45)]' : null,
        )}
        disabled={disabled}
        {...props}
        onClick={(event) => {
          props.onClick?.(event)
          setIsOpen((current) => !current)
        }}
        onKeyDown={(event) => {
          props.onKeyDown?.(event)
          if (event.key === 'Escape') {
            setIsOpen(false)
            return
          }
          if (event.key === 'ArrowDown' || event.key === 'Enter' || event.key === ' ') {
            event.preventDefault()
            setIsOpen(true)
          }
        }}
        ref={buttonRef}
        role="combobox"
        type="button"
      >
        <span className="block min-w-0 flex-1 truncate" title={buttonLabel}>
          {buttonLabel}
        </span>
      </button>
      <svg
        aria-hidden
        className={cn(
          'pointer-events-none absolute top-1/2 right-3 size-3 -translate-y-1/2 text-[var(--color-text-muted)] transition-transform',
          isOpen ? 'rotate-180' : null,
        )}
        fill="none"
        viewBox="0 0 12 12"
      >
        <path
          d="m3 4.5 3 3 3-3"
          stroke="currentColor"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="1.5"
        />
      </svg>
      {isOpen ? (
        <div
          className="absolute top-full right-0 left-0 z-50 mt-1 max-h-64 overflow-auto rounded-md border border-[var(--color-panel-border)] bg-white py-1 shadow-[0_10px_30px_rgba(26,28,25,0.12)]"
          id={menuId}
          role="menu"
        >
          {options.map((option) => (
            <label
              className="flex h-9 cursor-pointer items-center gap-2 px-3 text-sm whitespace-nowrap text-[var(--color-text)] hover:bg-[var(--color-hover-muted)]"
              key={option.value}
            >
              <input
                checked={selectedValues.has(option.value)}
                className="size-4 accent-[var(--color-primary)]"
                onChange={() => toggleOption(option.value)}
                type="checkbox"
              />
              <span>{option.label}</span>
            </label>
          ))}
        </div>
      ) : null}
    </div>
  )
}
